import React from 'react';
import { useMediaQuery, useTheme } from '@mui/material';
import { 
  format, 
  startOfMonth, 
  endOfMonth, 
  eachDayOfInterval, 
  isSameMonth, 
  isToday, 
  isWeekend, 
  getDay 
} from 'date-fns';
import { formatTime12h, esCitaGrupal } from '../../utils/calendarHelpers';
import { esFeriadoPeruano, obtenerNombreFeriado } from '../../utils/feriadosPeruanos';
import { isMobileDevice } from '../../utils/isAndroidWebView';

// NOTA: Esta vista solo se usa en desktop. En móvil se fuerza DayView.

// Componente para celda de día individual
const DayCell = React.memo(({ day, events, currentDate, onDateClick, onEventClick, colorConfig }) => {
  const isLowPerf = isMobileDevice();
  const isCurrentMonth = day ? isSameMonth(day, currentDate) : false;
  const isCurrentDay = day ? isToday(day) : false;
  const isWeekendDay = day ? isWeekend(day) : false;
  
  const dateStr = day ? format(day, 'yyyy-MM-dd') : null;
  const esFeriado = day ? esFeriadoPeruano(dateStr) : false;
  const nombreFeriado = esFeriado ? obtenerNombreFeriado(dateStr) : null;
  
  const dayEvents = day ? events.filter(event => 
    event.fecha === format(day, 'yyyy-MM-dd')
  ) : [];

  if (!day) {
    return (
      <div style={{
        width: '100%',
        height: '120px',
        backgroundColor: 'transparent',
        boxSizing: 'border-box'
      }} />
    );
  }
  
  // Estilos simplificados para mejor rendimiento
  let backgroundColor = '#fff';
  let borderStyle = '1px solid #e2e8f0';
  let textColor = 'inherit';
  
  if (esFeriado) {
    backgroundColor = '#ffebee';
    borderStyle = '2px solid #ef5350';
    textColor = '#d32f2f';
  } else if (isCurrentDay) {
    backgroundColor = '#e3f2fd';
    borderStyle = '2px solid #2196f3';
  } else if (isWeekendDay) {
    backgroundColor = '#f5f5f5';
  }
  
  const cellStyle = {
    width: '100%',
    height: isLowPerf ? '100px' : '120px',
    border: borderStyle,
    position: 'relative',
    overflow: 'hidden',
    padding: '6px',
    cursor: esFeriado ? 'not-allowed' : 'pointer',
    boxSizing: 'border-box',
    background: backgroundColor,
    color: textColor,
    borderRadius: '8px',
    transition: isLowPerf ? 'none' : 'all 0.2s ease'
  };
  
  const dayNumberStyle = {
    position: 'absolute',
    top: '8px',
    left: '8px',
    fontSize: isLowPerf ? '14px' : '16px',
    fontWeight: isCurrentDay ? '700' : '600',
    color: esFeriado ? '#d32f2f' : (isCurrentMonth ? 'inherit' : '#aaa'),
    lineHeight: 1
  };
  
  const eventsContainerStyle = {
    position: 'absolute',
    top: '28px',
    left: '6px',
    right: '6px',
    bottom: '6px',
    overflow: 'hidden'
  };
  
  return (
    <div 
      style={cellStyle}
      onClick={() => {
        if (!esFeriado) {
          onDateClick(day);
        }
      }}
      title={esFeriado ? `Feriado: ${nombreFeriado}` : `${format(day, 'd MMMM yyyy')} - Click para crear cita`}
    >
      <div style={dayNumberStyle}>
        {format(day, 'd')}
        {esFeriado && ' 🎊'}
      </div>
      
      <div style={eventsContainerStyle}>
        {/* Indicador de feriado cuando no hay eventos */}
        {esFeriado && dayEvents.length === 0 && (
          <div style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            fontSize: '10px',
            fontWeight: 'bold',
            color: '#d32f2f',
            textAlign: 'center',
            width: '100%'
          }}>
            FERIADO
            <br />
            <span style={{ fontSize: '8px', opacity: 0.8 }}>
              {nombreFeriado}
            </span>
          </div>
        )}
        
        {/* Eventos */}
        {dayEvents.slice(0, 3).map((event) => (
          <div 
            key={event.id} 
            style={{
              background: event.color,
              color: 'white',
              padding: '3px 6px',
              borderRadius: '4px',
              marginBottom: '2px',
              fontSize: '10px',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              cursor: 'pointer',
              border: esCitaGrupal(event) ? '2px solid #1976d2' : 'none',
              fontWeight: esCitaGrupal(event) ? 'bold' : '500',
              opacity: esFeriado ? 0.7 : 1
            }}
            onClick={(e) => {
              e.stopPropagation();
              onEventClick(event);
            }}
          >
            {formatTime12h(event.horaInicio)} - {event.title}
          </div>
        ))}
        
        {dayEvents.length > 3 && (
          <div style={{ 
            fontSize: '10px', 
            color: esFeriado ? '#d32f2f' : '#666',
            fontWeight: '500',
            textAlign: 'center',
            padding: '2px'
          }}>
            +{dayEvents.length - 3} más
          </div>
        )}
        
        {/* Nombre del feriado cuando hay eventos */}
        {esFeriado && dayEvents.length > 0 && (
          <div style={{
            position: 'absolute',
            bottom: '2px',
            left: '2px',
            right: '2px',
            fontSize: '7px',
            fontWeight: 'bold',
            color: '#b71c1c',
            textAlign: 'center',
            background: 'rgba(255, 255, 255, 0.9)',
            borderRadius: '2px',
            padding: '1px'
          }}>
            🎊 {nombreFeriado}
          </div>
        )}
      </div>
    </div>
  );
});

const MonthView = ({
  currentDate,
  events,
  onDateClick,
  onEventClick,
  colorConfig
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isLowPerf = isMobileDevice();

  const monthStart = startOfMonth(currentDate);
  const monthEnd = endOfMonth(currentDate);
  const days = eachDayOfInterval({ start: monthStart, end: monthEnd });
  
  const weekDays = ['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'];
  
  const weeks = [];
  let week = [];
  
  const firstDayOfMonth = monthStart.getDay();
  let startOffset;
  if (firstDayOfMonth === 0) {
    startOffset = 0;
  } else {
    startOffset = firstDayOfMonth - 1;
  }
  
  for (let i = 0; i < startOffset; i++) {
    week.push(null);
  }
  
  days.forEach((day) => {
    if (getDay(day) === 0) return;
    week.push(day);
    if (week.length === 6) {
      weeks.push(week);
      week = [];
    }
  });
  
  if (week.length > 0) {
    for (let i = week.length; i < 6; i++) {
      week.push(null);
    }
    weeks.push(week);
  }
  
  return (
    <div style={{ width: '100%' }}>
      {/* Encabezado con días de la semana */}
      <div style={{ 
        display: 'flex',
        width: '100%',
        background: colorConfig.shouldUseSpecialtyColors ? colorConfig.headerColor : '#f8f9fa',
        color: colorConfig.shouldUseSpecialtyColors ? colorConfig.headerTextColor : '#1a202c',
        borderRadius: '8px 8px 0 0',
        marginBottom: '4px'
      }}>
        {weekDays.map((day, index) => (
          <div key={day} style={{
            flex: '1 1 0',
            padding: isMobile ? '8px 4px' : '12px 8px',
            textAlign: 'center',
            fontWeight: '600',
            fontSize: isMobile ? '0.8rem' : '0.9rem',
            background: index === 5 ? 'rgba(0,0,0,0.05)' : 'transparent'
          }}>
            {day}
          </div>
        ))}
      </div>
      
      {/* Semanas del mes */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
        {weeks.map((week, weekIndex) => (
          <div key={weekIndex} style={{ 
            display: 'flex',
            gap: '4px',
            height: isMobile ? '90px' : '120px'
          }}>
            {week.map((day, dayIndex) => (
              <div key={dayIndex} style={{ flex: '1 1 0' }}>
                <DayCell 
                  day={day}
                  events={events}
                  currentDate={currentDate}
                  onDateClick={onDateClick}
                  onEventClick={onEventClick}
                  colorConfig={colorConfig}
                />
              </div>
            ))}
          </div>
        ))}
      </div>
      
      {/* Leyenda - solo en desktop */}
      {!isLowPerf && (
        <div style={{ 
          marginTop: '16px', 
          padding: '16px', 
          background: '#f5f5f5',
          borderRadius: '8px',
          border: '1px solid #e0e0e0'
        }}>
          <div style={{ fontSize: '14px', fontWeight: '600', marginBottom: '8px', color: '#333' }}>
            Leyenda:
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px' }}>
            {[
              { color: '#e3f2fd', border: '2px solid #2196f3', label: 'Día actual' },
              { color: '#f5f5f5', border: '1px solid #e0e0e0', label: 'Fin de semana' },
              { color: '#ffebee', border: '2px solid #ef5350', label: '🎊 Feriado' },
              { color: '#fff', border: '1px solid #e0e0e0', label: 'Día laborable' }
            ].map((item, index) => (
              <div key={index} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <div style={{ 
                  width: '16px', 
                  height: '12px', 
                  background: item.color,
                  border: item.border,
                  borderRadius: '3px'
                }} />
                <span style={{ fontSize: '12px', color: '#666' }}>{item.label}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default MonthView;
